import { DaysIndicator } from '@/components/displays/DaysIndicator';
import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { useSchool, useSession } from '@/providers/session/SessionProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatDatePart, formatTimePart } from '@/utils/format.date';
import { useApolloClient } from '@apollo/client';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel, GridPaginationModel, GridRowParams, GridRowSelectionModel, GridSortModel, getGridStringOperators, useGridApiRef } from '@mui/x-data-grid';
import { useNotifications } from '@toolpad/core';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { GET_CLASSROOMS, GetClassroomsResponse } from '../../gql';
import { ClassroomData } from '../../types/class';
import { parseFilterModel } from './ResultsClassroomTab.filter';

const defaultSortModel: { field: string; sort: 'asc' | 'desc' } = { field: 'endTime', sort: 'desc' };

interface ResultsClassroomTabProps {
    initialState?: {
        pagination?: { paginationModel: GridPaginationModel };
    };

    rowSelectionModel: GridRowSelectionModel;
    onRowSelectionModelChange: (model: GridRowSelectionModel) => void;

    compileResultsEnabled: boolean;
}

export const ResultsClassroomTab: FC<ResultsClassroomTabProps> = ({ initialState, rowSelectionModel, onRowSelectionModelChange, compileResultsEnabled }) => {
    // provider
    const { t } = useTranslation();
    const { session } = useSession();
    const { school } = useSchool();
    const notifications = useNotifications();
    const navigate = useNavigate();
    const client = useApolloClient();

    const [loading, setLoading] = React.useState(true);

    const [data, setData] = React.useState<{ count: number; rows: ClassroomData[] } | undefined>(undefined);
    const [queryOption, setQueryOption] = React.useState<{ pagination?: GridPaginationModel; sort?: GridSortModel; filter?: GridFilterModel } | undefined>(
        initialState?.pagination?.paginationModel ? { pagination: initialState.pagination.paginationModel } : undefined,
    );
    const gridApiRef = useGridApiRef();

    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: 'testroomName',
                headerName: t('lesson.label.classroomName'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 2,
            },
            {
                field: 'lessonName',
                headerName: t('lesson.label.lessonName'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'className',
                headerName: t('class.label.className'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 200,
                flex: 1,
                renderCell: (params) => {
                    const name = params.value || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={2} alignItems='center' height='100%'>
                            <Chip
                                size='medium'
                                sx={{
                                    '& .MuiChip-avatar': {
                                        color: text,
                                    },
                                }}
                                avatar={
                                    <InitialalsAvatar
                                        sx={{
                                            bgcolor: bg,
                                        }}
                                    >
                                        {getInitials(name)}
                                    </InitialalsAvatar>
                                }
                                label={`${name}`}
                            />
                        </Stack>
                    );
                },
            },
            {
                field: 'schoolYear',
                headerName: t('class.table.schoolYear'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'endDate',
                headerName: t('homework.deadlineDate'),
                sortable: true,
                filterable: false,
                minWidth: 160,
                valueGetter: (value, row) => row.endTime,
                renderCell: (params) => {
                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <DaysIndicator date={params.value}>
                                <Typography variant='body2'>{formatDatePart(params.value)}</Typography>
                            </DaysIndicator>
                        </Stack>
                    );
                },
            },
            {
                field: 'endTime',
                headerName: t('homework.deadlineTime'),
                sortable: true,
                filterable: false,
                minWidth: 140,
                flex: 1,
                renderCell: (params) => {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                ...(params.value
                                    ? {
                                          justifyContent: 'space-between',
                                      }
                                    : {
                                          justifyContent: 'flex-end',
                                      }),
                            }}
                        >
                            <Typography variant='body2'>{formatTimePart(params.value)}</Typography>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/results/classrooms/${params.row.id}`, { state: { classroomId: params.row.id }, viewTransition: true });
                                }}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ],
        [t, navigate],
    );

    const refresh = React.useCallback(async () => {
        if (!queryOption?.pagination || !school?.id || !session?.token) {
            setData(undefined);
            return;
        }

        setLoading(true);

        const filters = queryOption.filter ? parseFilterModel(queryOption.filter) : [];

        await client
            .query<GetClassroomsResponse>({
                query: GET_CLASSROOMS,
                fetchPolicy: 'network-only',
                variables: {
                    skip: queryOption.pagination.page * queryOption.pagination.pageSize,
                    take: queryOption.pagination.pageSize,
                    orderBy: queryOption.sort?.[0] ? [{ [queryOption.sort[0].field]: queryOption.sort[0].sort }] : [{ [defaultSortModel.field]: defaultSortModel.sort }],
                    where: {
                        AND: [
                            {
                                endTime: {
                                    lt: new Date().toISOString(),
                                },
                            },
                            {
                                class: {
                                    school: { id: { equals: school?.id } },
                                },
                            },
                            ...filters,
                        ],
                    },
                },
            })
            .then((response) => {
                setData({
                    count: response.data.testroomsCount,
                    rows: response.data.testrooms.map((testroom) => ({
                        id: testroom.id,
                        testroomName: testroom.testroomName,
                        lessonName: testroom.lessonVersion.lessonName,
                        endTime: testroom.endTime,
                        className: testroom.class.className,
                        schoolYear: testroom.class.schoolYear,
                    })),
                });
            })
            .catch((error) => {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            });
        setLoading(false);
    }, [client, notifications, queryOption, school?.id, session?.token]);

    /**
     * refresh data when
     *   - pagination model changed
     *   - sort model changed
     *   - school changed
     *   - session changed (not likely)
     */
    React.useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <DataGrid
            loading={loading}
            columns={columns}
            autoPageSize
            rows={data?.rows}
            filterMode='server'
            sortingMode='server'
            paginationMode='server'
            rowCount={data?.count || 0}
            initialState={{
                pagination: initialState?.pagination,
                sorting: { sortModel: [defaultSortModel] },
            }}
            onPaginationModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, pagination: model } : { pagination: model }));
                history.replaceState({ ...history.state, ...model }, '');
            }}
            onSortModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, sort: model } : { sort: model }));
            }}
            onFilterModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, filter: model } : { filter: model }));
            }}
            sx={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                // disable header focus style
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none',
                },
                // disable cell selection style
                '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                },
                // pointer cursor on ALL rows
                '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                },
            }}
            onRowClick={(params: GridRowParams) => {
                navigate(`/results/classrooms/${params.row.id}`, { state: { classroomId: params.row.id }, viewTransition: true });
            }}
            showColumnVerticalBorder={false}
            slotProps={{
                loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                },
            }}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            checkboxSelection={compileResultsEnabled}
            keepNonExistentRowsSelected={compileResultsEnabled}
            disableRowSelectionOnClick={true}
            hideFooterSelectedRowCount
            apiRef={gridApiRef}
        />
    );
};
